<template>
  <div class="new-incident-panel">
    <div class="new-incident-header">
      <v-icon small color="var(--kln-accent)" class="mr-2">mdi-alert-plus</v-icon>
      <span class="new-incident-title">Crear incidente</span>
      <v-spacer />
      <v-btn icon x-small @click="onCancel">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>

    <div class="new-incident-body">
      <!-- Tipo de incidente (required) -->
      <div class="field-group">
        <label class="field-label">Tipo de incidente <span class="req">*</span></label>
        <v-autocomplete
          v-model="form.tipoIncidente"
          :items="incidentTypes"
          item-text="incidentCNIName"
          item-value="incidentCNIId"
          :loading="loadingCatalogs"
          outlined dense hide-details
          clearable
          no-data-text="Sin catálogo disponible"
          :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 260 }"
        />
      </div>

      <!-- Ubicación (lat, lon) -->
      <div class="field-group">
        <label class="field-label">Ubicación <span class="req">*</span></label>
        <v-text-field
          :value="coordsDisplay"
          outlined dense hide-details readonly
          prepend-inner-icon="mdi-map-marker"
        />
      </div>

      <!-- Tipo de dispositivo -->
      <div class="field-group">
        <label class="field-label">Tipo de dispositivo <span class="req">*</span></label>
        <v-autocomplete
          v-model="form.idTipoDispositivo"
          :items="deviceTypes"
          item-text="desc"
          item-value="identifier"
          :loading="loadingCatalogs"
          outlined dense hide-details
          no-data-text="Sin catálogo disponible"
          :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 260 }"
        />
      </div>

      <!-- Identificador del dispositivo -->
      <div class="field-group">
        <label class="field-label">Identificador del dispositivo</label>
        <v-text-field
          v-model="form.idDispositivo"
          outlined dense hide-details
        />
      </div>

      <!-- Nombre(s) del reportante -->
      <div class="field-group">
        <label class="field-label">Nombre(s) del reportante</label>
        <v-text-field
          v-model="form.nombreCiudadano"
          outlined dense hide-details
        />
      </div>

      <!-- Apellido paterno / materno -->
      <div class="field-row">
        <div class="field-group field-group--grow">
          <label class="field-label">Apellido paterno</label>
          <v-text-field
            v-model="form.paternoCiudadano"
            outlined dense hide-details
          />
        </div>
        <div class="field-group field-group--grow">
          <label class="field-label">Apellido materno</label>
          <v-text-field
            v-model="form.maternoCiudadano"
            outlined dense hide-details
          />
        </div>
      </div>

      <!-- Descripción -->
      <div class="field-group">
        <label class="field-label">Descripción</label>
        <v-textarea
          v-model="form.descripcion"
          outlined dense hide-details
          rows="3"
          auto-grow
        />
      </div>

      <div v-if="errorMessage" class="new-incident-error">
        <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
        <span>{{ errorMessage }}</span>
      </div>
    </div>

    <div class="new-incident-footer">
      <v-btn text small @click="onCancel">Cancelar</v-btn>
      <v-btn
        color="var(--kln-accent)"
        small
        :loading="saving"
        :disabled="!canSubmit || saving"
        @click="onSubmit"
      >
        <v-icon left small>mdi-content-save</v-icon>
        Crear
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import cadCatalogsService from '@/util/cad-catalogs.service'

const DEFAULT_DEVICE_TYPE = '47' // KALAN — alineado al legacy NewIncidentForm.vue

export default {
  name: 'NewIncidentPanel',

  props: {
    seed: {
      type: Object,
      default: null, // { latitude, longitude, idTipoDispositivo? }
    },
  },

  data() {
    return {
      loadingCatalogs: false,
      saving: false,
      errorMessage: '',
      incidentTypes: [],
      deviceTypes: [],
      form: {
        tipoIncidente: null,
        latitude: null,
        longitude: null,
        idTipoDispositivo: DEFAULT_DEVICE_TYPE,
        idDispositivo: '',
        nombreCiudadano: '',
        paternoCiudadano: '',
        maternoCiudadano: '',
        descripcion: '',
      },
    }
  },

  computed: {
    ...mapGetters('auth', ['getUser']),
    coordsDisplay() {
      if (this.form.latitude == null || this.form.longitude == null) return ''
      return `${Number(this.form.latitude).toFixed(6)}, ${Number(this.form.longitude).toFixed(6)}`
    },
    canSubmit() {
      return !!(this.form.tipoIncidente
        && this.form.latitude != null
        && this.form.longitude != null
        && this.form.idTipoDispositivo)
    },
    cadCenter() {
      return (this.getUser && this.getUser.attributes && this.getUser.attributes.cad_center) || ''
    },
    cadUser() {
      return (this.getUser && this.getUser.attributes && this.getUser.attributes.cad_user) || ''
    },
    // Defensa en profundidad: el menú contextual ya gatea el botón "Crear
    // incidente" con este permiso, pero si alguien dispara la action
    // openNewIncident desde otro lugar (debug console, nueva integración),
    // el panel se monta. Aquí evitamos que el POST llegue al backend.
    hasCreatePermission() {
      var a = this.getUser && this.getUser.attributes
      return !!(a && a.cad_user && a.cad_permission_create_incident)
    },
  },

  watch: {
    seed: {
      immediate: true,
      handler(s) {
        if (!s) return
        this.form.latitude = s.latitude
        this.form.longitude = s.longitude
        if (s.idTipoDispositivo) this.form.idTipoDispositivo = s.idTipoDispositivo
      },
    },
  },

  created() {
    this.loadCatalogs()
  },

  methods: {
    ...mapActions('map-v2', ['closeNewIncident']),

    async loadCatalogs() {
      if (!this.cadCenter) {
        this.errorMessage = 'El usuario no tiene cad_center configurado; no se pueden cargar los catálogos.'
        return
      }
      this.loadingCatalogs = true
      try {
        const [inc, dev] = await Promise.all([
          cadCatalogsService.getIncidentTypes(this.cadCenter),
          cadCatalogsService.getDeviceTypes(this.cadCenter),
        ])
        this.incidentTypes = inc
        this.deviceTypes = dev
      } finally {
        this.loadingCatalogs = false
      }
    },

    onCancel() {
      this.closeNewIncident()
    },

    async onSubmit() {
      if (!this.canSubmit || this.saving) return
      if (!this.hasCreatePermission) {
        this.errorMessage = 'No tienes permiso para crear incidentes (cad_permission_create_incident).'
        return
      }
      this.errorMessage = ''
      this.saving = true
      const payload = {
        origen: 'KALAN',
        origenNombre: 'KALAN',
        origenId: this.cadUser,
        type: this.form.tipoIncidente,
        description: (this.form.descripcion || '').trim(),
        latitude: this.form.latitude,
        longitude: this.form.longitude,
        reportantName: (this.form.nombreCiudadano || '').trim(),
        reportantLastName1: (this.form.paternoCiudadano || '').trim(),
        reportantLastName2: (this.form.maternoCiudadano || '').trim(),
        idTipoDispositivo: this.form.idTipoDispositivo,
        idDispositivo: this.form.idDispositivo || '',
      }
      try {
        await cadCatalogsService.createIncident(payload)
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({
            title: 'Incidente creado correctamente',
            color: 'success',
            icon: 'mdi-check-circle-outline',
          })
        }
        this.closeNewIncident()
      } catch (err) {
        const msg = (err && err.response && err.response.data && (err.response.data.message || err.response.data.error))
          || 'No se pudo crear el incidente. Intenta de nuevo.'
        this.errorMessage = msg
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.new-incident-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--kln-bg-surface);
  border-left: 1px solid var(--kln-border-dim);
}

.new-incident-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid var(--kln-border-dim);
  flex-shrink: 0;
}

.new-incident-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--kln-text, #fff);
  letter-spacing: 0.02em;
}

.new-incident-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.new-incident-body::-webkit-scrollbar { width: 6px; }
.new-incident-body::-webkit-scrollbar-thumb {
  background: var(--kln-border-dim);
  border-radius: 3px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-group--grow {
  flex: 1 1 0;
  min-width: 0;
}

.field-row {
  display: flex;
  gap: 8px;
}

.field-label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--kln-text-muted);
}

.req {
  color: #f44336;
  margin-left: 2px;
}

.new-incident-error {
  display: flex;
  align-items: center;
  font-size: 12px;
  padding: 8px 10px;
  background: rgba(244, 67, 54, 0.08);
  border-left: 3px solid #f44336;
  border-radius: 4px;
  line-height: 1.4;
  color: #f44336;
}

.new-incident-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid var(--kln-border-dim);
  background: rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}
</style>
